import ErrorFallback from '@/components/ErrorFallback';
import Logo from '@/components/svg/Logo';
import MobileLogo from '@/components/svg/MobileLogo';
import settings from '@/constants/constants';
import { useSubmitForMedicalRecordsConsent } from '@/hooks/useSubmitForMedicalRecordsConsent';
import { PageWrapper } from '@/pages/components/Branding.styles';
import { ControlledSignaturePad } from '@/pages/components/ControlledSignaturePad';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, CircularProgress, Container, Link, TextField } from '@montugroup/design-system';
import { ALT_CUSTOMER_SERVICE_NUMBER_DISPLAY, ALT_CUSTOMER_SERVICE_NUMBER_INTL } from '@montugroup/frontend-common';
import type { PropsWithChildren } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_FORM_VALUES, FIELD_CONFIG } from './MedicalRecordsConsent.config';
import {
  CheckboxContainer,
  CheckField,
  Content,
  FieldsContainer,
  HalfWidthField,
  HalfWidthFieldsContainer,
  Heading,
  LogoContainer,
  LogoContainerMobile,
  MandatoryText,
  PreHeading,
  SectionDivider,
  SubmitButton,
  Text,
  Title,
} from './MedicalRecordsConsent.styles';
import type { ConsentMedicalRecordsFormValues } from './MedicalRecordsConsent.types';
import { ConsentMedicalRecordsSchema } from './MedicalRecordsConsent.types';

const MandatoryLabel = ({ children }: PropsWithChildren) => (
  <>
    {children}
    <MandatoryText component="span" aria-hidden sx={(theme) => ({ marginLeft: theme.spacing(1) })}>
      *
    </MandatoryText>
  </>
);

export const MedicalRecordsConsent = () => {
  const [searchParams] = useSearchParams();
  const { isSuccess, isPending, isError } = useSubmitForMedicalRecordsConsent();
  const paramData = searchParams.get('data');
  const rawParamKind = searchParams.get('kind');
  const paramKind = rawParamKind === 'patient-code' || rawParamKind === 'consent-uuid' ? rawParamKind : undefined; // for typing purposes

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ConsentMedicalRecordsFormValues>({
    resolver: zodResolver(ConsentMedicalRecordsSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  });
  const hasPreviousClinic = watch('previousClinic.hasPreviousClinic');
  const isRepresentative = watch('representative.isRepresentative');
  const signatureDisplayDate = new Date().toLocaleDateString();

  // If no 'data' in url params, display a 404 error message
  if (!paramData) {
    return (
      <Container maxWidth="md" sx={{ paddingTop: 25 }}>
        <ErrorFallback />;
      </Container>
    );
  }

  const onSubmit = async (formData: ConsentMedicalRecordsFormValues) => {
    console.log('formData = ', formData);
    console.log('paramKind = ', paramKind);
  };

  return (
    <PageWrapper>
      <Content component="form" onSubmit={handleSubmit(onSubmit)}>
        <LogoContainerMobile>
          <MobileLogo />
        </LogoContainerMobile>
        <LogoContainer>
          <Logo />
        </LogoContainer>
        <Title variant={'h1'}>Request for your health information</Title>
        <Text variant={'body1'} sx={(theme) => ({ marginBottom: theme.spacing(3) })}>
          This consent form lets Alternaleaf gather your medical records from your GP, or another provider outside
          Alternaleaf, and share them with your Alternaleaf doctor.
        </Text>
        <Text variant={'body1'} sx={(theme) => ({ marginBottom: theme.spacing(3) })}>
          This may include health summaries, letters of support, clinical records, discharge letters, or pathology
          reports.
        </Text>
        <Heading variant={'h3'}>Your privacy</Heading>
        <Text variant={'body1'}>
          We take your privacy seriously and want you to understand how your information is used. Please see our{' '}
          <Link href={settings.videoConsult.privacyPolicyUrl} target="_blank" rel="noopener noreferrer" color="primary">
            Privacy Policy
          </Link>{' '}
          for more details.
        </Text>

        <SectionDivider aria-hidden />

        <PreHeading>Practitioner</PreHeading>
        <Heading variant={'h3'}>Your health practitioner&apos;s details</Heading>
        <Text variant={'body1'} sx={(theme) => ({ marginBottom: theme.spacing(5) })}>
          Please tell us which practitioner you&apos;d like us to request records from (for example, your GP, a
          specialist, or an allied health practitioner such as a physiotherapist, osteopath, or psychologist).
          We&apos;ll copy you into all communication with them.
        </Text>
        <Text variant={'body1'} sx={(theme) => ({ marginBottom: theme.spacing(6) })}>
          Fields marked with <MandatoryText component="span">*</MandatoryText> are required
        </Text>

        <FieldsContainer>
          <TextField
            label={<MandatoryLabel>{FIELD_CONFIG.practitionerType.label}</MandatoryLabel>}
            {...register('practitionerType')}
            error={Boolean(errors.practitionerType)}
            helperText={errors.practitionerType?.message || FIELD_CONFIG.practitionerType.helperText}
          />
          <TextField
            label={<MandatoryLabel>{FIELD_CONFIG.practitionerFullName.label}</MandatoryLabel>}
            {...register('practitionerFullName')}
            error={Boolean(errors.practitionerFullName)}
            helperText={errors.practitionerFullName?.message || FIELD_CONFIG.practitionerFullName.helperText}
          />
          <TextField
            label={<MandatoryLabel>{FIELD_CONFIG.practitionerClinicName.label}</MandatoryLabel>}
            {...register('practitionerClinicName')}
            error={Boolean(errors.practitionerClinicName)}
            helperText={errors.practitionerClinicName?.message}
          />
          <TextField
            label={<MandatoryLabel>{FIELD_CONFIG.practitionerClinicAddress.label}</MandatoryLabel>}
            {...register('practitionerClinicAddress')}
            error={Boolean(errors.practitionerClinicAddress)}
            helperText={errors.practitionerClinicAddress?.message || FIELD_CONFIG.practitionerClinicAddress.helperText}
          />
          <HalfWidthFieldsContainer>
            <HalfWidthField
              label={<MandatoryLabel>{FIELD_CONFIG.practitionerClinicPhone.label}</MandatoryLabel>}
              {...register('practitionerClinicPhone')}
              error={Boolean(errors.practitionerClinicPhone)}
              helperText={errors.practitionerClinicPhone?.message}
            />
            <HalfWidthField
              label={FIELD_CONFIG.practitionerClinicEmail.label}
              {...register('practitionerClinicEmail')}
              error={Boolean(errors.practitionerClinicEmail)}
              helperText={errors.practitionerClinicEmail?.message}
            />
          </HalfWidthFieldsContainer>
        </FieldsContainer>

        <SectionDivider aria-hidden />

        <PreHeading>If applicable</PreHeading>
        <Heading variant={'h3'}>Moving from another cannabis clinic</Heading>
        <Controller
          name="previousClinic.hasPreviousClinic"
          control={control}
          render={({ field }) => (
            <CheckboxContainer
              control={<CheckField {...field} checked={field.value} />}
              label={FIELD_CONFIG.previousClinicHasPreviousClinic.label}
              sx={(theme) => ({ marginBottom: hasPreviousClinic ? theme.spacing(4) : 0 })}
            />
          )}
        />
        {hasPreviousClinic && (
          <HalfWidthFieldsContainer>
            <HalfWidthField
              label={<MandatoryLabel>{FIELD_CONFIG.previousClinicName.label}</MandatoryLabel>}
              {...register('previousClinic.name')}
              error={Boolean(errors.previousClinic?.name)}
              helperText={errors.previousClinic?.name?.message}
            />
            <HalfWidthField
              label={<MandatoryLabel>{FIELD_CONFIG.previousClinicEmail.label}</MandatoryLabel>}
              {...register('previousClinic.email')}
              error={Boolean(errors.previousClinic?.email)}
              helperText={errors.previousClinic?.email?.message}
            />
          </HalfWidthFieldsContainer>
        )}

        <SectionDivider aria-hidden />

        <PreHeading>If applicable</PreHeading>
        <Heading variant={'h3'}>Signing on someone&apos;s behalf</Heading>
        <Controller
          name="representative.isRepresentative"
          control={control}
          render={({ field }) => (
            <CheckboxContainer
              control={<CheckField {...field} checked={field.value} />}
              label={FIELD_CONFIG.representativeIsRepresentative.label}
              sx={(theme) => ({ marginBottom: isRepresentative ? theme.spacing(4) : 0 })}
            />
          )}
        />
        {isRepresentative && (
          <HalfWidthFieldsContainer>
            <HalfWidthField
              label={<MandatoryLabel>{FIELD_CONFIG.representativeFullName.label}</MandatoryLabel>}
              {...register('representative.fullName')}
              error={Boolean(errors.representative?.fullName)}
              helperText={errors.representative?.fullName?.message}
            />
            <HalfWidthField
              label={<MandatoryLabel>{FIELD_CONFIG.representativeRelationship.label}</MandatoryLabel>}
              {...register('representative.relationship')}
              error={Boolean(errors.representative?.relationship)}
              helperText={
                errors.representative?.relationship?.message || FIELD_CONFIG.representativeRelationship.helperText
              }
            />
          </HalfWidthFieldsContainer>
        )}

        <SectionDivider aria-hidden />

        <PreHeading>Consent</PreHeading>
        <Heading variant={'h3'}>Your consent and signature</Heading>
        <Text variant={'body1'} sx={(theme) => ({ marginBottom: theme.spacing(10) })}>
          I consent to Alternaleaf requesting my health information and records from the practitioner(s) named above,
          for the purpose of providing me with health services. I also consent to those practitioner(s) disclosing this
          information to Alternaleaf.
        </Text>

        <ControlledSignaturePad
          control={control}
          fieldName="signature"
          label={<MandatoryLabel>{FIELD_CONFIG.signature.label}</MandatoryLabel>}
          clearSignatureText="Reset and try again"
        />
        <TextField
          disabled
          label="Date"
          defaultValue={signatureDisplayDate}
          sx={(theme) => ({ width: 'fit-content', marginBottom: theme.spacing(7) })}
        />

        {isError && (
          <Alert severity="error">
            An error has occurred sending consent. Please try again or contact{' '}
            <Link href={`tel:${ALT_CUSTOMER_SERVICE_NUMBER_INTL}`}>{ALT_CUSTOMER_SERVICE_NUMBER_DISPLAY}</Link> for more
            help.
          </Alert>
        )}
        {isSuccess && <Alert severity="success">Your consent has been successfully submitted.</Alert>}

        <SubmitButton
          type="submit"
          variant="contained"
          size="large"
          disabled={isPending}
          startIcon={isPending ? <CircularProgress size={16} /> : null}
        >
          Submit consent
        </SubmitButton>
      </Content>
    </PageWrapper>
  );
};
